import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Store, MapPin, Phone, Mail, FileText, Building2,
  RefreshCw, CheckCircle, Info, CreditCard, Receipt
} from 'lucide-react';
import { storeSyncAPI, storesAPI } from '@/services/api';
import { formatDate } from '@/utils/dateFormat';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';

const StoreInformation = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [storeInfo, setStoreInfo] = useState(null);
  const [businessSettings, setBusinessSettings] = useState(null);
  const [gstInvoiceSettings, setGstInvoiceSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);

  const storeId = user?.entity_id;

  const fetchStoreInfo = useCallback(async () => {
    if (!storeId) return;

    setLoading(true);
    try {
      const [storeResponse, gstResponse] = await Promise.all([
        storeSyncAPI.getStoreInfo(storeId),
        storesAPI.getGstInvoiceSettings(storeId).catch(() => ({ data: null }))
      ]);
      setStoreInfo(storeResponse.data.store);
      setBusinessSettings(storeResponse.data.business_settings);
      setLastSynced(storeResponse.data.last_synced);
      setGstInvoiceSettings(gstResponse.data);
    } catch (error) {
      console.error('Error fetching store info:', error);
      toast({
        title: "Error",
        description: "Failed to fetch store information",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [storeId, toast]);

  useEffect(() => {
    fetchStoreInfo();
  }, [fetchStoreInfo]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await fetchStoreInfo();
      toast({
        title: "Sync Complete",
        description: "Store information has been synced from HQ",
      });
    } catch (error) {
      toast({
        title: "Sync Failed",
        description: "Could not sync store information",
        variant: "destructive"
      });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-500 mx-auto mb-2" />
          <p className="text-gray-500">Loading store information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="store-information-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Store className="w-7 h-7 text-emerald-600" />
            Store Information
          </h1>
          <p className="text-gray-500 mt-1">
            View store details synced from HQ (Read-only)
          </p>
        </div>
        <div className="flex items-center gap-4">
          {lastSynced && (
            <span className="text-sm text-gray-500">
              Last synced: {formatDate(lastSynced)}
            </span>
          )}
          <Button 
            onClick={handleSync} 
            disabled={syncing}
            className="bg-emerald-600 hover:bg-emerald-700 gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Store Info'}
          </Button>
        </div>
      </div>

      {/* Info Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5" />
        <div>
          <p className="text-sm text-blue-800 font-medium">Information is managed by HQ</p>
          <p className="text-sm text-blue-600 mt-1">
            Store details can only be modified from HQ. Click "Sync Store Info" to get the latest updates.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Store Details Card */}
        <Card className="shadow-lg">
          <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50 border-b">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-600" />
              Store Details
            </CardTitle>
            <CardDescription>Basic store information</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">Store Code</label>
                <p className="font-semibold text-lg text-emerald-600">{storeInfo?.code || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">Store Name</label>
                <p className="font-semibold text-lg">{storeInfo?.name || '-'}</p>
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Address
              </label>
              <p className="text-gray-700 mt-1">
                {typeof storeInfo?.address === 'object' ? (
                  <>
                    {storeInfo?.address?.address_line1 || '-'}
                    {storeInfo?.address?.address_line2 && `, ${storeInfo.address.address_line2}`}
                    {storeInfo?.address?.city && `, ${storeInfo.address.city}`}
                    {storeInfo?.address?.pincode && ` - ${storeInfo.address.pincode}`}
                  </>
                ) : (
                  <>
                    {storeInfo?.address || '-'}
                    {storeInfo?.city && `, ${storeInfo.city}`}
                    {storeInfo?.pincode && ` - ${storeInfo.pincode}`}
                  </>
                )}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">State</label>
                <p className="font-medium">
                  {typeof storeInfo?.address === 'object' ? storeInfo?.address?.state : storeInfo?.state || 'Tamil Nadu'}
                </p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">Pincode</label>
                <p className="font-medium">
                  {typeof storeInfo?.address === 'object' ? storeInfo?.address?.pincode : storeInfo?.pincode || '-'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
                  <Phone className="w-3 h-3" /> Contact
                </label>
                <p className="font-medium">
                  {typeof storeInfo?.contact === 'object' ? storeInfo?.contact?.phone : storeInfo?.contact || '-'}
                </p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
                  <Mail className="w-3 h-3" /> Email
                </label>
                <p className="font-medium text-sm">
                  {typeof storeInfo?.contact === 'object' ? storeInfo?.contact?.email || '-' : storeInfo?.email || '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tax & License Card */}
        <Card className="shadow-lg">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Tax & License Information
            </CardTitle>
            <CardDescription>GST and regulatory details</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wide">GSTIN</label>
              <p className="font-mono font-semibold text-lg text-blue-600">
                {storeInfo?.gstin || 'Not Available'}
              </p>
              {storeInfo?.gstin && (
                <Badge variant="outline" className="mt-1 text-xs">
                  <CheckCircle className="w-3 h-3 mr-1" /> Verified
                </Badge>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">PAN Number</label>
                <p className="font-mono font-medium">{storeInfo?.pan || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">FSSAI License</label>
                <p className="font-mono font-medium text-sm">{storeInfo?.fssai_license || '-'}</p>
              </div>
            </div>

            <div className="pt-4 border-t">
              <label className="text-xs text-gray-500 uppercase tracking-wide">GST State Code</label>
              <p className="font-medium">
                {storeInfo?.gstin ? storeInfo.gstin.substring(0, 2) : '-'}
                {storeInfo?.state && ` (${storeInfo.state})`}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Company Branding Card */}
        <Card className="shadow-lg lg:col-span-2">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 border-b">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-600" />
              Company Branding (From HQ)
            </CardTitle>
            <CardDescription>Organization logo and branding</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-center gap-6">
              {businessSettings?.logo_url ? (
                <div className="w-24 h-24 rounded-lg border-2 border-gray-200 overflow-hidden bg-white flex items-center justify-center p-2">
                  <img 
                    src={businessSettings.logo_url} 
                    alt="Company Logo" 
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ) : (
                <div className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                  <Building2 className="w-8 h-8 text-gray-400" />
                </div>
              )}
              <div>
                <h3 className="font-semibold text-xl text-gray-800">
                  {businessSettings?.company_name || 'SY-Store'}
                </h3>
                <p className="text-gray-500 mt-1">
                  Currency: {businessSettings?.currency_symbol || '₹'} (INR)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* GST Invoice Settings Card (From HQ) */}
        <Card className="shadow-lg lg:col-span-2">
          <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b">
            <CardTitle className="text-lg flex items-center gap-2">
              <Receipt className="w-5 h-5 text-amber-600" />
              A4 GST Invoice Settings (From HQ)
            </CardTitle>
            <CardDescription>Settings for generating A4 GST Tax Invoices</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {gstInvoiceSettings?.invoice_settings?.enabled ? (
              <div className="space-y-6">
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                    <CheckCircle className="w-3 h-3 mr-1" /> Enabled
                  </Badge>
                  <span className="text-sm text-gray-600">A4 GST Invoice generation is enabled for this store</span>
                </div>

                {/* Bank Details */}
                {gstInvoiceSettings?.bank_details?.bank_name && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-800 flex items-center gap-2 mb-3">
                      <CreditCard className="w-4 h-4 text-blue-600" />
                      Bank Details
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="text-xs text-gray-500 uppercase tracking-wide">Bank Name</label>
                        <p className="font-medium">{gstInvoiceSettings.bank_details.bank_name}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 uppercase tracking-wide">Account Number</label>
                        <p className="font-mono font-medium">{gstInvoiceSettings.bank_details.account_number}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 uppercase tracking-wide">IFSC Code</label>
                        <p className="font-mono font-medium">{gstInvoiceSettings.bank_details.ifsc_code}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 uppercase tracking-wide">Branch</label>
                        <p className="font-medium">{gstInvoiceSettings.bank_details.branch || '-'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Invoice Settings */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-800 flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4 text-amber-600" />
                    Invoice Settings
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wide">Signatory Name</label>
                      <p className="font-medium">{gstInvoiceSettings.invoice_settings?.signatory_name || '-'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wide">Show Bank Details</label>
                      <p className="font-medium">{gstInvoiceSettings.invoice_settings?.show_bank_details !== false ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                  
                  {gstInvoiceSettings.invoice_settings?.terms_and_conditions && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <label className="text-xs text-gray-500 uppercase tracking-wide">Terms & Conditions</label>
                      <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">
                        {gstInvoiceSettings.invoice_settings.terms_and_conditions}
                      </p>
                    </div>
                  )}
                  
                  {gstInvoiceSettings.invoice_settings?.footer_note && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <label className="text-xs text-gray-500 uppercase tracking-wide">Footer Note</label>
                      <p className="mt-1 text-sm text-gray-600 italic">
                        {gstInvoiceSettings.invoice_settings.footer_note}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">A4 GST Invoice Not Enabled</p>
                <p className="text-sm mt-1">Contact HQ to enable GST Invoice generation for this store</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StoreInformation;
